/**
 * INCSUBFeedbackDetailsCard Component
 * 
 * A detailed feedback display card that shows user-submitted feedback including title, expression,
 * review text, reviewer details, and an acknowledgment toggle (like/thumbs-up feature).
 * 
 * Props:
 * - `feedback`: Object containing feedback details (title, expression, reviewer info, etc.)
 * - `acknowledge`: Function to acknowledge or unacknowledge the feedback (triggered on toggle).
 * - `handleFunc`: Function to control the visibility of the feedback card (usually to close it).
 * 
 * State:
 * - `isAcknowledged`: Boolean state indicating whether the current user has acknowledged the feedback.
 * 
 * Side Effects:
 * - Uses `useEffect` to determine if the logged-in user has already acknowledged the feedback on initial render.
 * 
 * Example Usage:
 * <INCSUBFeedbackDetailsCard 
 *    feedback={feedbackObj} 
 *    acknowledge={acknowledgeHandler} 
 *    handleFunc={toggleCard}
 * />
 * 
 * Dependencies:
 * - CSS: `feedbackDetailsCard.css`
 * 
 * @component
 */


import React, { useEffect, useState } from 'react';
import './feedbackDetailsCard.css';

function INCSUBFeedbackDetailsCard({ feedback, acknowledge, handleFunc }) {
    // Local state to track if the current user acknowledged the feedback
    const [isAcknowledged, setIsAcknowledged] = useState(false);

    // Get current logged-in user ID from local storage
    const userId = JSON.parse(window.localStorage.getItem('userData')).userId;

    // Utility function to format a date to 'dd MMM yyyy' (e.g., 01 Jan 2023)
    const formateDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: 'short',
            year: "numeric"
        }).format(date);
    };

    // On component mount, check if the current user has already acknowledged the feedback
    useEffect(() => {
        if (feedback && feedback.feedbackAcknowledgedUserListDto.length > 0) {
            // Set acknowledgement to true if user is found in acknowledgement list
            const acknowledged = feedback.feedbackAcknowledgedUserListDto.some(user => user.userId === userId);
            setIsAcknowledged(acknowledged);
        }
    }, [feedback]);

    // Handler to toggle acknowledgement status
    const handleAcknowledge = (feedbackId) => {
        acknowledge({ userId, feedbackId, flag: !isAcknowledged });
    };

    return (
        // Outer container that closes the modal/card when clicked outside
        <div className='INCSUBFeedbackDetailsCard_ctn' onClick={() => handleFunc(false)}>
            
            {/* Inner card, click is stopped from propagating to avoid closing */}
            <div className='INCSUBFeedbackDetailsCard_card' onClick={(e) => e.stopPropagation()}>
                
                {/* Feedback title and created date */}
                <div className='INCSUBFeedback_card_heading_date'>
                    <div className='INCSUBFeedback_card_heading'>
                        {feedback?.feedbackTitle}
                    </div>
                    <div className='INCSUBFeedback_card_date'>{formateDate(feedback?.createdAt)}</div>
                </div>

                {/* Emoji or expression representing the feedback */}
                <div className='INCSUBFeedback_card_review'>
                    {feedback?.feedbackExpression}
                </div>

                {/* Feedback textual description */}
                <div className='INCSUBFeedback_card_review_text'>
                    <p>{feedback?.feedbackText}</p>
                </div>

                {/* Reviewer section with profile image and name */}
                <div className='INCSUBFeedback_card_reviewer_info'>
                    <div className='INCSUBFeedback_card_reviewer_info_imgename'>
                        <div className='INCSUBFeedback_card_reviewer_info_imge'>
                            <img 
                                className='INCSUB_UserImgTag' 
                                src={feedback?.feedbackByImage || '/assets/icons/common/defaultImage.png'} 
                                alt="feedbackByImage" 
                            />
                        </div>
                        <div className='INCSUB_Feedbackreview_Nametype'>
                            <p className='INCSUB_Feedbackreview_Name'>{feedback?.feedbackBy}</p>
                            <p className='INCSUB_Feedbackreview_Nametype'>{'Member'}</p>
                        </div>
                    </div>

                    {/* Acknowledged user names and toggle button */}
                    <div className='INCSUB_Feedbackreview_comments'>
                        {/* Display list of acknowledged users */}
                        {feedback?.feedbackAcknowledgedUserListDto?.map((user, index) => 
                            feedback.feedbackAcknowledgedUserListDto.length - 1 > index 
                                ? `${user.userFullName}, ` 
                                : user.userFullName
                        )}

                        {/* Acknowledge/Unacknowledge button (Thumbs up icon) */}
                        {
                            !isAcknowledged ? <svg onClick={() => {
                                handleAcknowledge(feedback.feedbackId);

                            }} width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.9502 19.2894V19.289V12.773C7.9502 11.8968 8.23439 11.0442 8.76012 10.3431C8.76014 10.3431 8.76017 10.3431 8.7602 10.343L10.4401 8.10394L10.4402 8.10384C11.1607 7.14315 11.5502 5.97469 11.5502 4.77384V3.97344C11.5502 3.85409 11.5976 3.73963 11.682 3.65524C11.7664 3.57085 11.8808 3.52344 12.0002 3.52344C12.4378 3.52344 12.8575 3.69728 13.1669 4.00671C13.4764 4.31615 13.6502 4.73583 13.6502 5.17344V9.97344V10.7234H14.4002H18.6721C18.6721 10.7234 18.6722 10.7234 18.6722 10.7234C18.9163 10.7235 19.1573 10.7777 19.3779 10.8821C19.5985 10.9865 19.7932 11.1386 19.948 11.3273C20.1027 11.5161 20.2136 11.7369 20.2728 11.9737C20.3319 12.2105 20.3378 12.4574 20.29 12.6968C20.29 12.6968 20.2899 12.6968 20.2899 12.6968L18.85 19.8968L18.8499 19.8968C18.7752 20.271 18.573 20.6077 18.2779 20.8496C17.9827 21.0915 17.6129 21.2236 17.2313 21.2234H17.231H10.732C10.732 21.2234 10.7319 21.2234 10.7318 21.2234C10.1038 21.2232 9.48445 21.0769 8.9227 20.7962L8.86279 20.7662L8.86265 20.7661C8.58836 20.6291 8.35769 20.4183 8.19652 20.1575C8.03535 19.8966 7.95005 19.596 7.9502 19.2894Z" stroke="#4880FF" stroke-width="1.5" />
                                <path d="M3.1499 12.9719C3.1499 12.834 3.17706 12.6974 3.22983 12.5701C3.2826 12.4427 3.35994 12.3269 3.45744 12.2294C3.55494 12.1319 3.67069 12.0546 3.79808 12.0018C3.92548 11.949 4.06201 11.9219 4.1999 11.9219C4.33779 11.9219 4.47433 11.949 4.60172 12.0018C4.72911 12.0546 4.84486 12.1319 4.94236 12.2294C5.03987 12.3269 5.11721 12.4427 5.16998 12.5701C5.22274 12.6974 5.2499 12.834 5.2499 12.9719V20.1719C5.2499 20.4504 5.13928 20.7174 4.94236 20.9143C4.74545 21.1112 4.47838 21.2219 4.1999 21.2219C3.92143 21.2219 3.65435 21.1112 3.45744 20.9143C3.26053 20.7174 3.1499 20.4504 3.1499 20.1719V12.9719Z" fill="#4880FF" stroke="#4880FF" stroke-width="1.5" />
                            </svg> : <svg onClick={() => {
                                handleAcknowledge(feedback.feedbackId);
                                setIsAcknowledged(false)
                            }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.3999 12.5984C2.3999 12.3621 2.44646 12.128 2.53692 11.9096C2.62738 11.6912 2.75996 11.4928 2.92711 11.3256C3.09426 11.1585 3.29269 11.0259 3.51107 10.9355C3.72946 10.845 3.96352 10.7984 4.1999 10.7984C4.43628 10.7984 4.67035 10.845 4.88873 10.9355C5.10712 11.0259 5.30555 11.1585 5.47269 11.3256C5.63984 11.4928 5.77243 11.6912 5.86289 11.9096C5.95334 12.128 5.9999 12.3621 5.9999 12.5984V19.7984C5.9999 20.2758 5.81026 20.7337 5.47269 21.0712C5.13513 21.4088 4.67729 21.5984 4.1999 21.5984C3.72251 21.5984 3.26468 21.4088 2.92711 21.0712C2.58954 20.7337 2.3999 20.2758 2.3999 19.7984V12.5984ZM7.1999 12.398V18.914C7.19969 19.36 7.32377 19.7973 7.5582 20.1767C7.79263 20.5561 8.12814 20.8627 8.5271 21.062L8.5871 21.092C9.25296 21.4248 9.9871 21.5982 10.7315 21.5984H17.2307C17.7857 21.5987 18.3237 21.4065 18.753 21.0547C19.1822 20.7028 19.4763 20.2131 19.5851 19.6688L21.0251 12.4688C21.0947 12.1207 21.0862 11.7614 21.0001 11.417C20.9141 11.0725 20.7528 10.7514 20.5277 10.4769C20.3026 10.2023 20.0194 9.98108 19.6985 9.82918C19.3776 9.67728 19.0269 9.59847 18.6719 9.59844H14.3999V4.79844C14.3999 4.16192 14.147 3.55147 13.697 3.10138C13.2469 2.65129 12.6364 2.39844 11.9999 2.39844C11.6816 2.39844 11.3764 2.52487 11.1514 2.74991C10.9263 2.97495 10.7999 3.28018 10.7999 3.59844V4.39884C10.7999 5.43741 10.463 6.44798 9.8399 7.27884L8.1599 9.51804C7.53676 10.3489 7.1999 11.3595 7.1999 12.398Z" fill="#4880FF" />
                            </svg>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

export default INCSUBFeedbackDetailsCard;


